import { PsvProviderStatus } from "@api/shared-runtime/psv-review";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatRunDate, formatRunTime } from "../format-run-time";
import { WorkspacePsvStatusCell } from "../workspace-psv-status";
import type { WorkspaceProvider } from "../workspace-types";

type PsvStatus = NonNullable<WorkspaceProvider["psvStatus"]>;

export function VerificationStatusCell({ psv }: { psv: PsvStatus }) {
  return <WorkspacePsvStatusCell psv={psv} />;
}

function StackedLabel({ children }: { children: string }) {
  return (
    <div className="text-xs text-muted-foreground md:hidden">{children}</div>
  );
}

export function LastCompletedCell({ psv }: { psv: PsvStatus }) {
  if (!psv.lastCompletedAt) {
    return (
      <div>
        <StackedLabel>Last completed on</StackedLabel>
        <div aria-hidden="true">—</div>
        <div className="text-xs text-muted-foreground">No completed run</div>
      </div>
    );
  }
  return (
    <div>
      <StackedLabel>Last completed on</StackedLabel>
      <div className="whitespace-nowrap text-sm font-semibold">
        {formatRunDate(psv.lastCompletedAt)}
      </div>
      <div className="text-xs text-muted-foreground">
        {formatRunTime(psv.lastCompletedAt)}
      </div>
    </div>
  );
}

/** A provider never verified can be started from the roster. Once a run
 * exists, the roster only opens it: running again is on the provider's page,
 * beside the report it replaces. */
export function RunVerificationCell({
  provider,
  psv,
  onRunVerification,
  onOpen,
}: {
  provider: WorkspaceProvider;
  psv: PsvStatus;
  onRunVerification: (provider: WorkspaceProvider) => void;
  onOpen: (provider: WorkspaceProvider) => void;
}) {
  const name = provider.name || "provider";
  if (psv.status !== PsvProviderStatus.NotRun) {
    return (
      <Button
        variant="outline"
        size="sm"
        aria-label={`View verification for ${name}`}
        onClick={(event) => {
          event.stopPropagation();
          onOpen(provider);
        }}
      >
        View verification
      </Button>
    );
  }
  return (
    <div className="flex min-w-36 flex-col items-start gap-1">
      <Button
        size="sm"
        className="gap-2"
        disabled={!provider.capabilities.canEditCredentials}
        aria-label={`Run verification for ${name}`}
        onClick={(event) => {
          event.stopPropagation();
          onRunVerification(provider);
        }}
      >
        <Play className="size-3.5" aria-hidden="true" />
        Run verification
      </Button>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        aria-label={`View provider ${name}`}
        onClick={(event) => {
          event.stopPropagation();
          onOpen(provider);
        }}
      >
        View provider
      </Button>
    </div>
  );
}
